import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useClassName } from '@travpro/hooks';
import i18n from 'i18n-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import usePortal from 'hooks/usePortal';

/**
 * @typedef {object} ModalProps
 * @property {string} [id='modal__root'] String id for the modal. Used for the portal root id. Default is `'modal__root'`
 * @property {boolean} show Boolean to show the modal or not.
 * @property {Function} onHide Function to call when the modal is closed.
 * @property {boolean} [bgClose=false] Boolean to allow closing the modal by clicking the background. Default is `false`
 * @property {boolean} [hideCloseBtn=false] Boolean to hide the default close button. Default is `false`
 * @property {boolean} [contentScroll=false] Boolean to allow scrolling in the modal content. Default is `false`
 * @property {boolean} [autosize=false] Boolean to allow the modal to autosize based on content. Default is `false`
 * @property {JSX.Element} [children=null] Any content. Rendered in the modal body. Default is `null`
 */

/**
 * A reusable (controlled) modal component which is rendered inside a Portal.
 *
 * @param {ModalProps} props Props
 * @returns {JSX.Element} A modal component.
 */
const Modal = ({
  id = 'modal__root',
  show,
  onHide = null,
  bgClose = false,
  hideCloseBtn = false,
  contentScroll = false,
  autosize = false,
  children = null,
}) => {
  const c = useClassName('modal');

  const Portal = usePortal(id);

  // Wraps the onHide func param (if any) to trigger after closing the modal state.
  const closeModal = () => {
    if (onHide) onHide();
  };

  const handleBackgroundClick = () => {
    if (bgClose) closeModal();
  };

  const handleBackgroundKeydown = (e) => {
    if (bgClose && (e.key === 'Enter' || e.key === ' ')) closeModal();
  };

  // adds a class to the body to disable scrolling when the modal is open.
  // Todo: investigate alternative that does not require manually manipulating the DOM, without requiring each view to check for a modal manually.
  useEffect(() => {
    if (show) {
      document.body.classList.add('modal__parent--scroll-disabled');
    } else {
      document.body.classList.remove('modal__parent--scroll-disabled');
    }

    return () => {
      document.body.classList.remove('modal__parent--scroll-disabled');
    };
  }, [show]);

  return (
    <Portal rootId={id} prepend>
      {show && (
        <div {...c({ autosize: autosize })}>
          <div
            {...c('overlay', 'scroll-disabled')}
            onClick={handleBackgroundClick}
            onKeyDown={handleBackgroundKeydown}
            role="button"
            tabIndex="0"
            aria-label="Close modal"
          />
          <div {...c('content', { 'scroll-enabled': contentScroll })}>
            {!hideCloseBtn && (
              <button
                {...c('button', 'close')}
                type="button"
                onClick={closeModal}
                aria-label={i18n.translate('general.close')}
              >
                <FontAwesomeIcon icon={['fas', 'window-close']} />
              </button>
            )}
            <div {...c('body')}>{children}</div>
          </div>
        </div>
      )}
    </Portal>
  );
};

Modal.propTypes = {
  id: PropTypes.string,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  bgClose: PropTypes.bool,
  hideCloseBtn: PropTypes.bool,
  contentScroll: PropTypes.bool,
  autosize: PropTypes.bool,
  children: PropTypes.node,
};

export default Modal;
